import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  type EplanSafetyFunctionProposal,
  type SafetyFunctionWithArchitecture,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import {
  useProductSourceChunks,
  useProductSourceDownloadUrl,
  type ProductSource,
} from '@/shared/hooks/api/productSource'
import { cn } from '@/shared/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { ProductSourceChunksWithPdf } from '@/features/product-catalog/components/ProductSourceChunksWithPdf'
import { getProductSourcePreviewMode } from '@/features/product-catalog/utils/isRagIngestedProductSource'

/** Re-enable when E-plan page preview is ready for users. */
const SHOW_EPLAN_PAGE_PREVIEW = false

type ExtractFromEplanModalProps = {
  isOpen: boolean
  onClose: () => void
  productId: string | undefined
  eplanSource: ProductSource | null | undefined
  proposals: EplanSafetyFunctionProposal[]
  isExtracting: boolean
  isImporting: boolean
  progressMessage: string | null
  progressCurrent: number | null
  progressTotal: number | null
  extractionError: string | null
  importedEplanExtractedFunctionIds: ReadonlySet<string>
  onStartExtraction: (regenerate?: boolean) => void
  onImport: (
    selectedFunctionIds: string[],
    editedProposals: EplanSafetyFunctionProposal[]
  ) => void
  onProposalsChange: (proposals: EplanSafetyFunctionProposal[]) => void
}

function enumBadge(
  translate: (key: string, options?: Record<string, unknown>) => string,
  group: string,
  value: string | null | undefined
): string | null {
  if (value == null || value.trim() === '') {
    return null
  }
  return translate(`safetyFunctionsPage.enums.${group}.${value}`, {
    defaultValue: value,
  })
}

function ArchitectureBreakdown({
  proposal,
}: {
  proposal: EplanSafetyFunctionProposal
}) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const { components } = proposal.architecture

  if (components.length === 0) {
    return null
  }

  return (
    <div className='space-y-1'>
      {components.map((component, index) => {
        const functionTypeLabel = enumBadge(
          t,
          'functiontypes',
          component.functiontypes
        )
        const catLabel = enumBadge(t, 'cat', component.cat)
        const channelLabels = component.channels
          .map((ch) => enumBadge(t, 'channeltype', ch.channeltype) ?? ch.name)
          .filter(Boolean)
        return (
          <div
            key={index}
            className='flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground'
          >
            <span className='font-medium text-foreground'>
              {component.name}
            </span>
            {functionTypeLabel ? (
              <Badge variant='outline' className='py-0 text-[10px]'>
                {functionTypeLabel}
              </Badge>
            ) : null}
            {catLabel ? (
              <Badge variant='secondary' className='py-0 text-[10px]'>
                {catLabel}
              </Badge>
            ) : null}
            {component.missiontime ? (
              <span>{component.missiontime}</span>
            ) : null}
            {channelLabels.length > 0 ? (
              <span className='text-muted-foreground/70'>
                · {channelLabels.join(', ')}
              </span>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

type ProposalRowProps = {
  proposal: EplanSafetyFunctionProposal
  isSelected: boolean
  isAlreadyImported: boolean
  isDisabled: boolean
  productId: string | undefined
  eplanSource: ProductSource | null | undefined
  onToggle: (id: string) => void
  onUpdate: (id: string, patch: Partial<EplanSafetyFunctionProposal>) => void
}

function ProposalRow({
  proposal,
  isSelected,
  isAlreadyImported,
  isDisabled,
  productId,
  eplanSource,
  onToggle,
  onUpdate,
}: ProposalRowProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const [showPreview, setShowPreview] = useState(false)
  const previewPages = proposal.eplanPageLocation?.pages ?? []
  const firstPage = previewPages[0]

  const { data: chunksData } = useProductSourceChunks(
    productId,
    eplanSource?.id,
    {
      enabled:
        SHOW_EPLAN_PAGE_PREVIEW &&
        showPreview &&
        productId != null &&
        eplanSource != null,
    }
  )
  const { data: downloadUrlData } = useProductSourceDownloadUrl(
    productId,
    eplanSource?.filePathInBucket
  )

  const filteredChunks = useMemo(() => {
    if (previewPages.length === 0) {
      return chunksData?.chunks ?? []
    }
    const pageSet = new Set(previewPages)
    return (chunksData?.chunks ?? []).filter((chunk) => pageSet.has(chunk.page))
  }, [chunksData?.chunks, previewPages])

  const previewMode =
    eplanSource != null ? getProductSourcePreviewMode(eplanSource.type) : 'none'

  return (
    <Card className='overflow-hidden'>
      <div className='flex items-start gap-3 border-b bg-muted/30 px-4 py-3'>
        <Checkbox
          checked={isSelected}
          disabled={isDisabled || isAlreadyImported}
          onCheckedChange={() => {
            onToggle(proposal.eplanExtractedFunctionId)
          }}
          aria-label={proposal.name}
        />
        <div className='min-w-0 flex-1 space-y-1'>
          <div className='flex flex-wrap items-center gap-2'>
            <span className='font-medium'>{proposal.name}</span>
            <Badge variant='outline'>{proposal.type}</Badge>
            {isAlreadyImported ? (
              <Badge variant='secondary'>
                {t('safetyFunctionsPage.extractFromEplanModal.alreadyImported')}
              </Badge>
            ) : null}
          </div>
          {proposal.eplanReferenceId ? (
            <p className='text-xs text-muted-foreground'>
              {t('safetyFunctionsPage.extractFromEplanModal.referenceId', {
                referenceId: proposal.eplanReferenceId,
              })}
            </p>
          ) : null}
          {previewPages.length > 0 ? (
            <p className='text-xs text-muted-foreground'>
              {t('safetyFunctionsPage.extractFromEplanModal.pages', {
                pages: previewPages.join(', '),
              })}
            </p>
          ) : null}
        </div>
      </div>
      <div className='space-y-3 px-4 py-3'>
        <div className='grid gap-3 sm:grid-cols-2'>
          <div className='space-y-1'>
            <Label htmlFor={`name-${proposal.eplanExtractedFunctionId}`}>
              {t('safetyFunctionsPage.extractFromEplanModal.name')}
            </Label>
            <Input
              id={`name-${proposal.eplanExtractedFunctionId}`}
              value={proposal.name}
              disabled={isDisabled || isAlreadyImported}
              onChange={(event) => {
                onUpdate(proposal.eplanExtractedFunctionId, {
                  name: event.target.value,
                })
              }}
            />
          </div>
          <div className='space-y-1'>
            <Label htmlFor={`reference-${proposal.eplanExtractedFunctionId}`}>
              {t('safetyFunctionsPage.extractFromEplanModal.referenceIdLabel')}
            </Label>
            <Input
              id={`reference-${proposal.eplanExtractedFunctionId}`}
              value={proposal.eplanReferenceId ?? ''}
              disabled={isDisabled || isAlreadyImported}
              onChange={(event) => {
                onUpdate(proposal.eplanExtractedFunctionId, {
                  eplanReferenceId: event.target.value || null,
                })
              }}
            />
          </div>
        </div>
        <div className='grid gap-3 sm:grid-cols-2'>
          <div className='space-y-1'>
            <Label htmlFor={`type-${proposal.eplanExtractedFunctionId}`}>
              {t('safetyFunctionsPage.extractFromEplanModal.type')}
            </Label>
              <Select
                value={proposal.type}
                disabled={isDisabled || isAlreadyImported}
                onValueChange={(value) => {
                  if (!value) {
                    return
                  }
                  onUpdate(proposal.eplanExtractedFunctionId, {
                    type: value as EplanSafetyFunctionProposal['type'],
                  })
                }}
                items={[
                  { value: 'ISD', label: 'ISD' },
                  { value: 'SCP', label: 'SCP' },
                  { value: 'PPE', label: 'PPE' },
                  { value: 'OI', label: 'OI' },
                ]}
              >
              <SelectTrigger id={`type-${proposal.eplanExtractedFunctionId}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='ISD'>ISD</SelectItem>
                <SelectItem value='SCP'>SCP</SelectItem>
                <SelectItem value='PPE'>PPE</SelectItem>
                <SelectItem value='OI'>OI</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {proposal.type === 'SCP' ? (
            <div className='space-y-1'>
              <Label>
                {t(
                  'safetyFunctionsPage.extractFromEplanModal.dependsOnControlSystem'
                )}
              </Label>
              <Select
                value={
                  proposal.dependsOnControlSystem === true
                    ? 'yes'
                    : proposal.dependsOnControlSystem === false
                      ? 'no'
                      : 'unknown'
                }
                disabled={isDisabled || isAlreadyImported}
                onValueChange={(value) => {
                  if (!value) {
                    return
                  }
                  onUpdate(proposal.eplanExtractedFunctionId, {
                    dependsOnControlSystem:
                      value === 'yes' ? true : value === 'no' ? false : null,
                  })
                }}
                items={[
                  {
                    value: 'unknown',
                    label: t('safetyFunctionsPage.extractFromEplanModal.unknown'),
                  },
                  {
                    value: 'yes',
                    label: t('safetyFunctionsPage.extractFromEplanModal.yes'),
                  },
                  {
                    value: 'no',
                    label: t('safetyFunctionsPage.extractFromEplanModal.no'),
                  },
                ]}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='unknown'>
                    {t('safetyFunctionsPage.extractFromEplanModal.unknown')}
                  </SelectItem>
                  <SelectItem value='yes'>
                    {t('safetyFunctionsPage.extractFromEplanModal.yes')}
                  </SelectItem>
                  <SelectItem value='no'>
                    {t('safetyFunctionsPage.extractFromEplanModal.no')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          ) : null}
        </div>
        <div className='space-y-1'>
          <Label htmlFor={`description-${proposal.eplanExtractedFunctionId}`}>
            {t('safetyFunctionsPage.extractFromEplanModal.description')}
          </Label>
          <Textarea
            id={`description-${proposal.eplanExtractedFunctionId}`}
            value={proposal.description ?? ''}
            disabled={isDisabled || isAlreadyImported}
            onChange={(event) => {
              onUpdate(proposal.eplanExtractedFunctionId, {
                description: event.target.value || null,
              })
            }}
          />
        </div>
        <ArchitectureBreakdown proposal={proposal} />
        {SHOW_EPLAN_PAGE_PREVIEW && firstPage != null ? (
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='px-0'
            onClick={() => {
              setShowPreview((prev) => !prev)
            }}
          >
            {showPreview
              ? t('safetyFunctionsPage.extractFromEplanModal.hidePagePreview')
              : t('safetyFunctionsPage.extractFromEplanModal.showPagePreview')}
          </Button>
        ) : null}
        {SHOW_EPLAN_PAGE_PREVIEW && showPreview && filteredChunks.length > 0 ? (
          <ProductSourceChunksWithPdf
            chunks={filteredChunks}
            downloadUrl={downloadUrlData?.url}
            previewMode={previewMode}
          />
        ) : null}
      </div>
    </Card>
  )
}

export function ExtractFromEplanModal({
  isOpen,
  onClose,
  productId,
  eplanSource,
  proposals,
  isExtracting,
  isImporting,
  progressMessage,
  progressCurrent,
  progressTotal,
  extractionError,
  importedEplanExtractedFunctionIds,
  onStartExtraction,
  onImport,
  onProposalsChange,
}: ExtractFromEplanModalProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [hasStarted, setHasStarted] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setHasStarted(false)
      setSelectedIds(new Set())
      return
    }
    if (!hasStarted && !isExtracting && proposals.length === 0) {
      setHasStarted(true)
      onStartExtraction(false)
    }
  }, [isOpen, hasStarted, isExtracting, proposals.length, onStartExtraction])

  useEffect(() => {
    if (proposals.length === 0) {
      return
    }
    const selectable = proposals
      .map((proposal) => proposal.eplanExtractedFunctionId)
      .filter((id) => !importedEplanExtractedFunctionIds.has(id))
    setSelectedIds(new Set(selectable))
  }, [proposals, importedEplanExtractedFunctionIds])

  const toggleId = useCallback(
    (id: string) => {
      if (importedEplanExtractedFunctionIds.has(id)) {
        return
      }
      setSelectedIds((prev) => {
        const next = new Set(prev)
        if (next.has(id)) {
          next.delete(id)
        } else {
          next.add(id)
        }
        return next
      })
    },
    [importedEplanExtractedFunctionIds]
  )

  const updateProposal = useCallback(
    (id: string, patch: Partial<EplanSafetyFunctionProposal>) => {
      onProposalsChange(
        proposals.map((proposal) =>
          proposal.eplanExtractedFunctionId === id
            ? { ...proposal, ...patch }
            : proposal
        )
      )
    },
    [onProposalsChange, proposals]
  )

  const selectedList = useMemo(() => Array.from(selectedIds), [selectedIds])
  const isBusy = isExtracting || isImporting
  const canImport = selectedList.length > 0 && !isBusy && proposals.length > 0

  const handleImport = useCallback(() => {
    if (!canImport) {
      return
    }
    onImport(selectedList, proposals)
  }, [canImport, onImport, proposals, selectedList])

  const showProgress =
    isExtracting &&
    (progressMessage != null ||
      (progressCurrent != null && progressTotal != null))

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='flex max-h-[90vh] flex-col sm:max-w-3xl'>
        <DialogHeader>
          <DialogTitle>
            {t('safetyFunctionsPage.extractFromEplanModal.title')}
          </DialogTitle>
        </DialogHeader>

        <div className='min-h-0 flex-1 space-y-4 overflow-y-auto'>
          {showProgress ? (
            <div className='flex items-center gap-3 rounded-md border bg-muted/30 px-4 py-3 text-sm'>
              <Loader2 className='size-4 shrink-0 animate-spin' />
              <span>
                {progressMessage ??
                  (progressCurrent != null && progressTotal != null
                    ? t('safetyFunctionsPage.extractFromEplanModal.progress', {
                        current: progressCurrent,
                        total: progressTotal,
                      })
                    : t(
                        'safetyFunctionsPage.extractFromEplanModal.extracting'
                      ))}
              </span>
            </div>
          ) : null}

          {extractionError != null ? (
            <p className='text-sm text-destructive'>{extractionError}</p>
          ) : null}

          {!isExtracting &&
          extractionError == null &&
          proposals.length === 0 ? (
            <p className='py-6 text-center text-muted-foreground'>
              {t('safetyFunctionsPage.extractFromEplanModal.noFunctionsFound')}
            </p>
          ) : null}

          {!isExtracting && proposals.length > 0 ? (
            <div className='space-y-3'>
              <p className='text-sm text-muted-foreground'>
                {t('safetyFunctionsPage.extractFromEplanModal.selectHint')}
              </p>
              {proposals.map((proposal) => (
                <ProposalRow
                  key={proposal.eplanExtractedFunctionId}
                  proposal={proposal}
                  isSelected={selectedIds.has(
                    proposal.eplanExtractedFunctionId
                  )}
                  isAlreadyImported={importedEplanExtractedFunctionIds.has(
                    proposal.eplanExtractedFunctionId
                  )}
                  isDisabled={isBusy}
                  productId={productId}
                  eplanSource={eplanSource}
                  onToggle={toggleId}
                  onUpdate={updateProposal}
                />
              ))}
            </div>
          ) : null}
        </div>

        <DialogFooter className='flex-col gap-2 sm:flex-row sm:justify-between'>
          <Button
            type='button'
            variant='outline'
            disabled={isBusy}
            onClick={() => {
              onStartExtraction(true)
            }}
          >
            <RefreshCw
              className={cn('mr-2 size-4', isExtracting && 'animate-spin')}
            />
            {t('safetyFunctionsPage.extractFromEplanModal.regenerate')}
          </Button>
          <div className='flex gap-2'>
            <Button
              type='button'
              variant='outline'
              onClick={onClose}
              disabled={isBusy}
            >
              {t('safetyFunctionsPage.extractFromEplanModal.cancel')}
            </Button>
            <Button type='button' disabled={!canImport} onClick={handleImport}>
              {isImporting ? (
                <Loader2 className='mr-2 size-4 animate-spin' />
              ) : null}
              {t('safetyFunctionsPage.extractFromEplanModal.importSelected', {
                count: selectedList.length,
              })}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function buildImportedEplanExtractedFunctionIds(
  safetyFunctions: SafetyFunctionWithArchitecture[]
): Set<string> {
  const ids = new Set<string>()
  for (const safetyFunction of safetyFunctions) {
    if (
      safetyFunction.source === 'EPLAN' &&
      safetyFunction.eplanExtractedFunctionId != null &&
      safetyFunction.eplanExtractedFunctionId !== ''
    ) {
      ids.add(safetyFunction.eplanExtractedFunctionId)
    }
  }
  return ids
}
